import React, { FC, ReactElement, useState } from "react";
import style from "./style.scss";
import { ThemeBuilderSectionLine } from "./ThemeBuilderSectionLine";
import { WidgetValuePicker, WidgetValuePickerChildrenProps, WidgetValuePickerProps } from "./WidgetValuePicker";
import { IconButton, Input } from "@xapp/components";

import { ColorResult, SketchPicker } from "react-color";
import { parseRgb } from "utils/color";

export interface WidgetColorPickerProps extends Pick<WidgetValuePickerProps<string>, "overrideLabel" | "defaultLabel" | "passBackDefault"> {
    readonly label?: string;
    readonly value: string | undefined;
    readonly defaultValue?: string | undefined;
    readonly fineControls?: boolean;
    readonly disabled?: boolean;
    readonly headingContent?: ReactElement;
    onChange(value: string): void;
}

export const WidgetColorPickerRaw: FC<WidgetValuePickerChildrenProps<string>> = props => {

    return (
        <Input
            className={style.colorPickerValue}
            type="color"
            value={colorToHex(props.value) || ""}
            disabled={props.override || props.disabled}
            onChange={(value) => props.onChange(value as string)}
        />
    );
};

const WidgetColorPickerInner: FC<WidgetColorPickerProps> = props => {

    const [displayColorPicker, setDisplayColorPicker] = useState(false);

    const fineControls = props.fineControls ?? false;

    const handleClose = () => {
        setDisplayColorPicker(false);
    };

    const handleChange = (color: ColorResult) => {
        props.onChange(convertColor(color)); // rgb
    };

    return (
        <ThemeBuilderSectionLine label={props.label ?? "Color"} headingContent={props.headingContent}>

            <WidgetValuePicker<string>
                value={props.value}
                defaultValue={props.defaultValue}
                children={WidgetColorPickerRaw}
                disabled={props.disabled}
                onChange={props.onChange}
                overrideLabel={props.overrideLabel}
                defaultLabel={props.defaultLabel}
                passBackDefault={props.passBackDefault}
            />

            {fineControls && (
                <div style={{ height: "100%", minWidth: "40px" }}>
                    <IconButton iconName="tune" onClick={() => setDisplayColorPicker(true)} />
                </div>
            )}

            {displayColorPicker && (
                <div className={style.rgbaPickerPopover}>
                    <div className={style.rgbaPickerCover} onClick={handleClose} />
                    <SketchPicker color={convertColor(props.value || "#ffffff")} onChange={handleChange} />
                </div>
            )}
        </ThemeBuilderSectionLine>
    );
};

function convertColor(input: string | ColorResult): string {
    // Check if the input is a string in hexadecimal format
    if (typeof input === 'string') {
        if (input.startsWith('#')) {
            // Convert hexadecimal to RGBA
            const r = parseInt(input.slice(1, 3), 16);
            const g = parseInt(input.slice(3, 5), 16);
            const b = parseInt(input.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, 1)`;
        } else if (input.startsWith('rgba')) {
            // If input is already in rgba format, return it unchanged
            return input;
        }
    } else if ('rgb' in input) {
        // Handle ColorResult object from react-color
        const { r, g, b, a } = input.rgb;
        if (a === 1) {
            // Convert to hexadecimal if alpha is 1
            const toHex = (x: number): string => x.toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        } else {
            // Otherwise return rgba format
            return `rgba(${r}, ${g}, ${b}, ${a})`;
        }
    }

    throw new Error(`Invalid color format: ${input}`);
}

/**
 * Converts either RGB or Hex color to Hex color
 *
 * @param color
 */
function colorToHex(color?: string): string | undefined {

    if (!color) {
        return undefined;
    }
    if (color?.startsWith('#')) {
        return color;
    } else {
        const rgba = parseRgb(color);
        if (rgba) {
            const { r, g, b } = rgba;
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    }

    return color;
}


export const WidgetColorPicker = React.memo(WidgetColorPickerInner);
